import {Occupancy} from "@variocube/app-api";
import {DateTime} from "luxon";
import {assertAmountRounding} from "./utils";

export interface BookingOptions {
    enableBooking: boolean;
    enablePayment?: PaymentOptions | false;
    enableBookingCode: boolean;
    enableEmail?: boolean;
    enablePhone?: boolean;
    enableDisplay?: boolean;
    enableRelease?: boolean;
    askReleaseLocker?: boolean;
    plans: BookingPlan[];
    accessibleFrom?: string;
    accessibleUntil?: string;
    accessibility?: Accessibility;
    termsUrl?: string;
    privacyPolicyUrl?: string;
    currency?: string;
    siteId?: number;
    siteName?: string;
    siteCubes?: string[];
    bookingUnit: BookingUnit;
    pricingMode: "Unit" | "Package";
    discounts?: Record<string, Discount>;
}

export type BookingUnit = "Days" | "Hours";
export type PricingMode = "Unit" | "Package";

export interface Discount {
    code?: string;
    type: 'Code'|'WestfieldLoyaltyService';
    value: number;
}

export interface Accessibility {
    [weekday: string]: { from: string, until: string };
}

export interface PaymentOptions {
    maxAmount: number;
    requireCardUid?: boolean;
}

export interface Dimensions {
    width?: number;
    height?: number;
    depth?: number;
}

export interface BookingPlan {
    boxType: string;
    dimensions?: Dimensions;
    units: Array<{
        from: number;
        price: number;
    }>;
    packages: Array<{
        quantity: number;
        price: number;
    }>;
}

export interface Availability {
    [boxType: string]: BoxTypeAvailability;
}

/**
 * Booking as created by the backend and stored in the cube storage.
 */
export interface Booking {
    uuid: string;
    bookingCode: string;
    bookingKey?: string;
    boxes: BookingBoxes;
    from: string;
    until: string;
    occupancies: BookingOccupancy[];
    name: string;
    email: string;
    phone: string;
    state: BookingState;
}

export type BookingState = 'Pending' | 'Canceled' | 'Confirmed';

export interface BookingBoxes {
    [boxType: string]: number;
}

export interface BookingOccupancy {
    cube: string;
    boxNumber: string;
    occupancy: string;
    boxType: string;
    ended?: string;
}

export interface Bookings {
    [bookingCode: string]: Booking;
}

export interface BoxTypeAvailability {
    max: number;
}

/**
 * Booking as created on the cube on stored into the occupancy content.
 */
export interface OccupancyBooking {
    /** The selected box type */
    boxType: string;

    /** The datetime (ISO 8601) from which the booking is valid. */
    from: string;

    /** The datetime (ISO 8601) until which the booking is valid.*/
    until: string;

    /** The datetime (ISO 8601) when the booking was created. */
    created?: string;

    /** The number of units */
    units: number;

    /** The verified phone number */
    phone?: string;

    /** The verified email address */
    email?: string;

    /** The amount that has been paid */
    amount: number;

    /** The card uid that was used to pay for this booking */
    cardUid?: string;

    /** The receipt text of the payment */
    receiptText: string;

    /** The locale */
    locale: string;

    /** The discount was used on this booking */
    discount?: Discount;
}

export interface Access {
    booking?: Booking;
    occupancies: Occupancy[];
}

export interface BookingOccupancyContent {
    booking: OccupancyBooking;
}

export function getOccupancyBooking(occupancy: Occupancy) {
    return occupancy.content?.booking as OccupancyBooking | undefined;
}

export interface Cart {
    boxType: string;
    units: number;
    amount: number;
    from: DateTime;
    until: DateTime;
    discount?: Discount;
}

export enum AccessStatus {
    None,
    Pending,
    Failed,
}

export function computeAmount(pricingMode: "Unit" | "Package", selectedPlan: BookingPlan, units: number, discount?: Discount) {
    let amount = 0;
    let finalAmount = 0;

    if (pricingMode == "Unit") {
        const unitsReverseSorted = [...selectedPlan.units].sort((a, b) => b.from - a.from);

        let total = 0;
        for (let i = units; i > 0; i--) {
            const line = unitsReverseSorted.find(unitPricingLine => unitPricingLine.from <= i);
            if (line) {
                total += line?.price;
            }
        }
        amount = total;
    } else {
        const packageLine = selectedPlan.packages.find(packageLine => packageLine.quantity == units);
        if (packageLine) {
            amount = packageLine.price;
        }
    }

    finalAmount = amount;
    if (discount && amount) {
        finalAmount = Math.round((amount - (discount.value * amount / 100)) * 100) / 100;
    }

    return {
        discount,
        amount: assertAmountRounding(amount),
        finalAmount: assertAmountRounding(finalAmount),
    };
}

export interface CubeTexts {
    discounts: Record<string, string>;
}